'use client';

import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { checkIfInLocalStorage } from '@eliadmin/core/src/utils/favorite-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FiHeart } from 'react-icons/fi';

type AddToFavoritesButtonProps = {
  slug: string;
  className?: {
    button?: string;
  };
  isFavorite?: boolean;
  productId?: number;
  showText?: boolean;
};

export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const hasCustomer = useCustomerStore(state => !!state.me?.authToken);
  const startedAsGuest = useRef(!hasCustomer);
  // Keep an already mounted mutation owner alive through logout; no lost onSettled callback.
  const [activated, setActivated] = useState(hasCustomer);
  useEffect(() => { if (hasCustomer) setActivated(true); }, [hasCustomer]);
  return hasCustomer || activated
    ? <CustomerFavoritesButton {...props} refetchOnMount={startedAsGuest.current} />
    : <GuestFavoritesButton {...props} />;
}

function GuestFavoritesButton(props: AddToFavoritesButtonProps) {
  const enabled = useGlobalStore(state => state.templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled);
  const router = useRouter();
  const [isFavorite, setFavorite] = useState(!!props.isFavorite);
  useEffect(() => {
    const sync = () => setFavorite(enabled && props.productId != null ? !!checkIfInLocalStorage(props.productId) : !!props.isFavorite);
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [enabled, props.productId, props.isFavorite]);
  return <FavoritesView {...props} isFavorite={isFavorite} onClick={event => {
    event.stopPropagation();
    if (!enabled) { router.push(ROUTES.SIGN_IN()); return; }
    if (props.productId == null) return;
    const stored = JSON.parse(localStorage.getItem('favoriteProducts') || '[]') as number[];
    const favorites = stored.includes(props.productId)
      ? stored.filter(id => id !== props.productId)
      : [...stored, props.productId];
    localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
    setFavorite(favorites.includes(props.productId));
    window.dispatchEvent(new Event('storage'));
  }} />;
}

function CustomerFavoritesButton(props: AddToFavoritesButtonProps & { refetchOnMount: boolean }) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const queryClient = useQueryClient();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const productQueryKey = [CacheKey.PRODUCT_DETAIL, locale, props.slug, me?.customer.id ?? 'guest'];

  const { data: product } = useQuery({
    queryKey: productQueryKey,
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: isInitialized && !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    refetchOnMount: props.refetchOnMount,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: addProductToFavorites,
    onSettled: (data, error) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCT_DETAIL, locale, props.slug] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.FAVORITE_PRODUCTS] });
      if (!error) {
        queryClient.setQueryData(productQueryKey, {
          ...product,
          data: {
            ...product?.data,
            is_favorite: !isFavorite,
          },
        });
      }
    },
  });

  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(
    (props.productId &&
      ((!me &&
        templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled &&
        checkIfInLocalStorage(props.productId)) ||
        product?.data.is_favorite ||
        props.isFavorite)) ??
      false,
  );

  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!authToken) {
      if (templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled) {
        const existingFavorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');

        if (existingFavorites.includes(props.productId)) {
          const updatedFavorites = existingFavorites.filter((id: number) => id !== props.productId);

          setIsFavorite(false);

          localStorage.setItem('favoriteProducts', JSON.stringify(updatedFavorites));
          window.dispatchEvent(new Event('storage'));
        } else {
          existingFavorites.push(props.productId);

          setIsFavorite(true);

          localStorage.setItem('favoriteProducts', JSON.stringify(existingFavorites));
          window.dispatchEvent(new Event('storage'));
        }
      } else {
        return router.push(ROUTES.SIGN_IN());
      }
    } else {
      mutate({ productSlug: props.slug, authToken, locale });
      setIsFavorite(!isFavorite);
    }
  }

  return <FavoritesView {...props} isFavorite={isFavorite} isMutating={isMutating} onClick={handleAddToFavorites} />;
}

function FavoritesView(props: AddToFavoritesButtonProps & {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isMutating?: boolean;
}) {
  const { t } = useTranslations();
  const { isFavorite, isMutating } = props;
  return (
    <Button
      size={'icon'}
      variant={'alternative-default'}
      onClick={props.onClick}
      className={cn(props.className?.button)}
      aria-label={isFavorite ? t('text532') : t('text531')}
      aria-pressed={!!isFavorite}
    >
      {isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="flex flex-row">
          <FiHeart
            className={cn(
              'h-5 w-5 cursor-pointer transition-colors duration-200 ease-in-out  hover:text-muted-foreground',
              'text-foreground',
              isFavorite
                ? 'fill-destructive-foreground stroke-destructive-foreground'
                : 'fill-none',
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {props.showText && (
            <span className="link ml-2 underline">{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
